<template>
  <div class="profile-page">
    <a-spin :spinning="loading" tip="加载中...">
      <template v-if="!loading">
        <!-- 订阅暂停警告 -->
        <a-alert
          v-if="isSubscriptionExpired"
          type="warning"
          show-icon
          banner
          style="margin-bottom: 16px;"
        >
          <template slot="message">
            <span>您的订阅已暂停，部分功能受限。请联系管理员续费以恢复完整功能。</span>
          </template>
        </a-alert>

        <!-- 用户信息卡片 -->
        <a-card :bordered="false" class="profile-card">
          <template slot="title">
            <div class="card-title-row">
              <div class="card-title-left">
                <div class="avatar-placeholder">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span class="card-title-text">个人信息</span>
              </div>
              <div class="card-title-actions">
                <a-button type="primary" size="small" @click="$router.push('/users/wode/edit')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 4px; vertical-align: -1px;">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  编辑信息
                </a-button>
                <a-button size="small" @click="$router.push('/users/wode/password')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 4px; vertical-align: -1px;">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  修改密码
                </a-button>
              </div>
            </div>
          </template>

          <a-descriptions :column="{ xs: 1, sm: 2, md: 3 }" size="middle" bordered>
            <a-descriptions-item label="用户名">
              {{ userInfo.user_name || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="角色">
              <a-tag v-if="userInfo.role" :color="roleColor(userInfo.role.code)">
                {{ userInfo.role.name }}
              </a-tag>
              <span v-else>—</span>
            </a-descriptions-item>
            <a-descriptions-item label="手机号">
              {{ userInfo.mobile || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="邮箱">
              {{ userInfo.user_mail || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="创建时间">
              {{ userInfo.created_at || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="更新时间">
              {{ userInfo.updated_at || '—' }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 租户信息卡片（仅租户用户可见） -->
        <a-card
          v-if="userInfo.tenant_id"
          :bordered="false"
          class="profile-card tenant-card"
        >
          <template slot="title">
            <div class="card-title-row">
              <div class="card-title-left">
                <div class="avatar-placeholder tenant-avatar">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span class="card-title-text">租户信息</span>
              </div>
            </div>
          </template>

          <a-descriptions :column="{ xs: 1, sm: 2, md: 3 }" size="middle" bordered>
            <a-descriptions-item label="租户名称">
              {{ tenantInfo.name || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="租户状态">
              <a-tag :color="tenantStatusColor">
                {{ tenantStatusLabel }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="订阅到期时间">
              <span :class="subscriptionExpiryClass">
                {{ subscriptionExpiredAtFormatted || '未订阅' }}
                <a-tag v-if="subscriptionWarning" color="orange" style="margin-left: 8px;">
                  剩余 {{ subscriptionStatus.daysRemaining }} 天
                </a-tag>
                <a-tag v-if="subscriptionExpired" color="red" style="margin-left: 8px;">
                  已过期
                </a-tag>
              </span>
            </a-descriptions-item>
            <a-descriptions-item label="创建时间">
              {{ tenantInfo.created_at || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="更新时间">
              {{ tenantInfo.updated_at || '—' }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 订阅记录卡片（仅租户用户可见） -->
        <a-card
          v-if="userInfo.tenant_id"
          :bordered="false"
          class="profile-card subscription-card"
        >
          <template slot="title">
            <div class="card-title-row">
              <div class="card-title-left">
                <div class="avatar-placeholder subscription-avatar">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <span class="card-title-text">订阅记录</span>
              </div>
            </div>
          </template>

          <a-table
            :columns="subscriptionColumns"
            :data-source="subscriptions"
            :loading="subscriptionsLoading"
            :pagination="subscriptionsPagination"
            rowKey="id"
            size="middle"
            @change="handleSubscriptionsTableChange"
          >
            <template slot="status" slot-scope="status, record">
              <a-tag :color="getSubscriptionStatusColor(status)">
                {{ record.status_text || getSubscriptionStatusText(status) }}
              </a-tag>
            </template>
            <template slot="started_at" slot-scope="text">
              {{ formatDateOnly(text) }}
            </template>
            <template slot="expired_at" slot-scope="text">
              {{ formatDateOnly(text) }}
            </template>
            <template slot="remarks" slot-scope="text">
              {{ text || '—' }}
            </template>
          </a-table>
        </a-card>
      </template>

      <!-- 加载失败 -->
      <a-result
        v-if="loadError"
        status="error"
        title="加载失败"
        :sub-title="loadError"
      >
        <template slot="extra">
          <a-button type="primary" @click="loadData">重新加载</a-button>
        </template>
      </a-result>
    </a-spin>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'WodeProfile',
  data() {
    return {
      loading: true,
      loadError: null,
      userInfo: {},
      tenantInfo: {},
      subscriptionStatus: {
        hasSubscription: false,
        status: 'none',
        expiredAt: null,
        daysRemaining: null,
        warning: false
      },
      // 订阅记录
      subscriptions: [],
      subscriptionsLoading: false,
      subscriptionsPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `共 ${total} 条`
      },
      subscriptionColumns: [
        { title: '服务名称', dataIndex: 'service_name', width: 140 },
        { title: '状态', dataIndex: 'status', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '开始时间', dataIndex: 'started_at', width: 130, scopedSlots: { customRender: 'started_at' } },
        { title: '到期时间', dataIndex: 'expired_at', width: 130, scopedSlots: { customRender: 'expired_at' } },
        { title: '备注', dataIndex: 'remarks', scopedSlots: { customRender: 'remarks' } },
        { title: '创建时间', dataIndex: 'created_at', width: 170 }
      ]
    };
  },
  computed: {
    ...mapGetters('main/user', ['info']),

    // 订阅是否已过期/暂停
    isSubscriptionExpired() {
      return this.info?.subscription?.subscriptionExpired === true;
    },

    // 租户状态颜色
    tenantStatusColor() {
      if (!this.tenantInfo.id) return 'default';
      // TenantStatus: 1=未订阅, 2=正常
      switch (this.tenantInfo.status) {
        case 1: return 'red';
        case 2: return 'green';
        default: return 'default';
      }
    },

    // 租户状态文本
    tenantStatusLabel() {
      if (!this.tenantInfo.id) return '—';
      switch (this.tenantInfo.status) {
        case 1: return '未订阅';
        case 2: return '正常';
        default: return '未知';
      }
    },

    // 订阅到期时间格式化
    subscriptionExpiredAtFormatted() {
      const raw = this.tenantInfo.subscription_expired_at;
      if (!raw) return null;
      // 已是 YYYY-MM-DD HH:mm:ss 格式（后端 transformer 处理），截取日期部分
      return raw.substring(0, 10);
    },

    // 订阅临期警告
    subscriptionWarning() {
      return this.subscriptionStatus.warning === true;
    },

    // 订阅已过期
    subscriptionExpired() {
      if (!this.subscriptionStatus.hasSubscription) return false;
      return this.subscriptionStatus.daysRemaining !== null && this.subscriptionStatus.daysRemaining <= 0;
    },

    // 订阅到期文字高亮样式
    subscriptionExpiryClass() {
      if (this.subscriptionExpired) return 'expiry-expired';
      if (this.subscriptionWarning) return 'expiry-warning';
      return '';
    }
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.loadError = null;

      try {
        // 获取用户详情（含 role、tenant 关联）
        const userId = this.info?.id;
        if (!userId) {
          this.loadError = '无法获取用户信息，请重新登录';
          this.loading = false;
          return;
        }

        const userRes = await this.$api.getTenantUserDetail(userId);
        if (!userRes || userRes.code !== 200) {
          this.loadError = userRes?.message || '获取用户信息失败';
          this.loading = false;
          return;
        }

        this.userInfo = userRes.data;

        // 如果有租户，并行获取租户详情和订阅状态
        if (this.userInfo.tenant_id) {
          const [tenantRes, subRes] = await Promise.all([
            this.$api.get_tenant_detail(this.userInfo.tenant_id).catch(() => null),
            this.$api.get_tenant_subscription_status(this.userInfo.tenant_id).catch(() => null)
          ]);

          if (tenantRes && tenantRes.code === 200) {
            this.tenantInfo = tenantRes.data;
          }

          if (subRes && subRes.code === 200) {
            this.subscriptionStatus = subRes.data;
          }

          // 并行加载订阅记录
          this.loadSubscriptions();
        }
      } catch (err) {
        console.error('[wode] load error:', err);
        this.loadError = '网络异常，请稍后重试';
      } finally {
        this.loading = false;
      }
    },

    // 角色颜色映射
    roleColor(code) {
      switch (code) {
        case 100: return 'purple';  // 超管
        case 200: return 'blue';    // 租户管理员
        case 300: return 'default'; // 普通用户
        default: return 'default';
      }
    },

    // 加载订阅记录
    async loadSubscriptions() {
      if (!this.userInfo.tenant_id) return;
      this.subscriptionsLoading = true;
      try {
        const res = await this.$api.get_subscriptions_by_tenant(this.userInfo.tenant_id, {
          page_no: this.subscriptionsPagination.current,
          page_size: this.subscriptionsPagination.pageSize
        });
        if (res && res.code === 200) {
          this.subscriptions = res.data || [];
          this.subscriptionsPagination.total = res.page && res.page.total || 0;
        }
      } catch (err) {
        console.error('[wode] load subscriptions error:', err);
      } finally {
        this.subscriptionsLoading = false;
      }
    },

    // 订阅记录分页切换
    handleSubscriptionsTableChange(pagination) {
      this.subscriptionsPagination.current = pagination.current;
      this.subscriptionsPagination.pageSize = pagination.pageSize;
      this.loadSubscriptions();
    },

    // 格式化日期（仅显示日期部分）
    formatDateOnly(dateStr) {
      if (!dateStr) return '—';
      // 后端 transformer 返回 YYYY-MM-DD HH:mm:ss 格式，截取日期部分
      return dateStr.substring(0, 10);
    },

    // 订阅状态颜色
    getSubscriptionStatusColor(status) {
      const colors = {
        1: 'green',   // 生效中
        2: 'red',     // 已过期
        3: 'default'  // 已取消
      };
      return colors[status] || 'default';
    },

    // 订阅状态文本
    getSubscriptionStatusText(status) {
      const texts = {
        1: '生效中',
        2: '已过期',
        3: '已取消'
      };
      return texts[status] || '未知';
    }
  }
};
</script>

<style scoped>
.profile-page {
  max-width: 1200px;
  margin: 0 auto;
}

.profile-card {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.profile-card >>> .ant-card-head {
  border-bottom: 1px solid #f0f0f0;
}

.profile-card >>> .ant-card-body {
  padding: 24px;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.card-title-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-title-text {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.card-title-actions {
  display: flex;
  gap: 8px;
}

.avatar-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.tenant-avatar {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.subscription-avatar {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

/* 订阅到期高亮 */
.expiry-warning {
  color: #fa8c16;
  font-weight: 600;
}

.expiry-expired {
  color: #f5222d;
  font-weight: 600;
}

/* Descriptions 样式增强 */
.profile-card >>> .ant-descriptions-item-label {
  color: #6b7280;
  font-weight: 500;
  min-width: 100px;
}

.profile-card >>> .ant-descriptions-item-content {
  color: #111827;
}

/* 订阅记录表格样式增强 */
.subscription-card >>> .ant-table-thead > tr > th {
  background: #fafafa;
  color: #6b7280;
  font-weight: 600;
  font-size: 13px;
}

.subscription-card >>> .ant-table-tbody > tr > td {
  font-size: 13px;
  color: #111827;
}

.subscription-card >>> .ant-table-pagination.ant-pagination {
  margin: 16px 0 0 0;
}

/* 响应式 */
@media (max-width: 576px) {
  .card-title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .card-title-actions {
    width: 100%;
  }

  .card-title-actions .ant-btn {
    flex: 1;
  }
}
</style>
